// @ts-nocheck
/**
 * Orders API Client
 * Clean API layer - HTTP calls, validation, transformation only
 * All filtering and pagination is server-side
 */

import {
  getHttpClient,
  isNumericId,
  normalizeEntity,
  coerceToNumberId,
} from '@spcc/core';

import type {
  Order,
  OrderStats,
  CreateOrderRequest,
  UpdateOrderRequest,
  OrdersFilter,
  PaginatedOrders,
  PaginationMeta,
} from '../../domain/types';

import type { OrderEvent } from '../../domain/types/order-timeline.types';

import {
  ORDER_ENDPOINTS,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
} from '../../domain/constants';

export interface BulkCancelFailure {
  orderId: string;
  message: string;
}

export interface BulkCancelOrdersResult {
  successCount: number;
  failureCount: number;
  succeededOrderIds: string[];
  failed: BulkCancelFailure[];
}

export interface BulkForceUpdateFailure {
  orderId: string;
  message: string;
}

export interface BulkForceUpdateStatusResult {
  successCount: number;
  failureCount: number;
  succeededOrderIds: string[];
  failed: BulkForceUpdateFailure[];
}

import {
  transformOrderResponseToDomain,
  transformOrdersResponseToDomain,
} from '../../api/transformers';
import { validateOrderStats } from '../../api/api-types';

function applyProductsFilter(
  params: URLSearchParams,
  products: string[],
): void {
  const filtered = products.map((p) => p.trim()).filter(Boolean);
  if (filtered.length === 0) return;

  const allNumeric = filtered.every(isNumericId);
  const idKey = allNumeric ? 'id' : 'documentId';

  filtered.forEach((value, index) => {
    params.set(`filters[variant][${idKey}][$in][${index}]`, value);
  });
}

function applyDeliveryAddressesFilter(
  params: URLSearchParams,
  deliveryAddresses: string[],
): void {
  const filtered = deliveryAddresses.map((a) => a.trim()).filter(Boolean);
  if (filtered.length === 0) return;

  const allNumeric = filtered.every(isNumericId);
  const idKey = allNumeric ? 'id' : 'documentId';

  filtered.forEach((value, index) => {
    params.set(`filters[$or][0][dropoff][${idKey}][$in][${index}]`, value);
    params.set(
      `filters[$or][1][sales_order][deliveryAddress][${idKey}][$in][${index}]`,
      value,
    );
    params.set(
      `filters[$or][2][dropoff][city][${idKey}][$in][${index}]`,
      value,
    );
    params.set(
      `filters[$or][3][sales_order][city][${idKey}][$in][${index}]`,
      value,
    );
  });
}

const API_SORTABLE_FIELDS: Record<string, string> = {
  createdAt: 'createdAt',
  orderDate: 'createdAt',
  deliveryDate: 'deliveryDate',
  orderNumber: 'orderNumber',
  status: 'orderStatus',
  quantity: 'quantity',
};

const CLIENT_SORTABLE_FIELDS = new Set(['product', 'totalAmount', 'tenant', 'deliveryDate']);

function getApiSortField(sortBy: string): string | null {
  return API_SORTABLE_FIELDS[sortBy] ?? null;
}

function sortOrdersClientSide(
  orders: Order[],
  sortBy: string,
  sortOrder: 'asc' | 'desc',
): Order[] {
  const direction = sortOrder === 'asc' ? 1 : -1;
  const sorted = [...orders];

  if (sortBy === 'deliveryDate') {
    sorted.sort((a, b) => {
      const aDate = a.deliveryDate ?? null;
      const bDate = b.deliveryDate ?? null;
      // Nulls always last regardless of sort direction
      if (aDate === null && bDate === null) return 0;
      if (aDate === null) return 1;
      if (bDate === null) return -1;
      return aDate.localeCompare(bDate) * direction;
    });
    return sorted;
  }

  if (sortBy === 'product') {
    sorted.sort((a, b) => {
      const aProduct = a.items[0]?.product?.en ?? '';
      const bProduct = b.items[0]?.product?.en ?? '';
      return aProduct.localeCompare(bProduct) * direction;
    });
    return sorted;
  }

  if (sortBy === 'totalAmount') {
    sorted.sort(
      (a, b) => (a.totalAmount.amount - b.totalAmount.amount) * direction,
    );
    return sorted;
  }

  if (sortBy === 'tenant') {
    sorted.sort(
      (a, b) =>
        (a.tenant?.name ?? '').localeCompare(b.tenant?.name ?? '') * direction,
    );
    return sorted;
  }

  return sorted;
}

// ============================================
// Query Builders
// ============================================

function buildPopulateParams(): URLSearchParams {
  const params = new URLSearchParams();

  // variant → sku only (categories are never used in orders transformer)
  params.set('populate[variant][populate][sku][fields][0]', 'documentId');
  params.set('populate[variant][populate][sku][fields][1]', 'name');

  // tenant: only the 4 fields used in the transformer
  params.set('populate[tenant][fields][0]', 'documentId');
  params.set('populate[tenant][fields][1]', 'name');
  params.set('populate[tenant][fields][2]', 'code');
  params.set('populate[tenant][fields][3]', 'tenantStatus');

  params.set('populate[dropoff][populate][city]', 'true');

  // factory: only the 4 fields used in the transformer
  params.set('populate[factory][fields][0]', 'documentId');
  params.set('populate[factory][fields][1]', 'name');
  params.set('populate[factory][fields][2]', 'code');
  params.set('populate[factory][fields][3]', 'sapPlantCode');

  // sales_order → factory (same field restriction)
  params.set('populate[sales_order][populate][factory][fields][0]', 'documentId');
  params.set('populate[sales_order][populate][factory][fields][1]', 'name');
  params.set('populate[sales_order][populate][factory][fields][2]', 'code');
  params.set('populate[sales_order][populate][factory][fields][3]', 'sapPlantCode');

  // sales_order → createdByUser: only fields used in the transformer
  params.set('populate[sales_order][populate][createdByUser][fields][0]', 'id');
  params.set('populate[sales_order][populate][createdByUser][fields][1]', 'username');
  params.set('populate[sales_order][populate][createdByUser][fields][2]', 'email');
  params.set('populate[sales_order][populate][createdByUser][fields][3]', 'fullName');

  params.set('populate[subCustomer]', 'true');
  return params;
}

function buildListParams(filter?: OrdersFilter): URLSearchParams {
  const params = buildPopulateParams();

  // Pagination (server-side)
  const page = filter?.page ?? 1;
  const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE;
  params.set('pagination[page]', String(page));
  params.set('pagination[pageSize]', String(pageSize));

  // Sorting (server-side)
  const sortBy = filter?.sortBy ?? 'createdAt';
  const sortOrder = filter?.sortOrder ?? 'desc';
  const apiSortField = getApiSortField(sortBy);
  const effectiveSortField = apiSortField ?? 'createdAt';
  params.set('sort[0]', `${effectiveSortField}:${sortOrder}`);

  // Status filter (server-side)
  // Handle both array and string status for backwards compatibility
  if (filter?.status) {
    const statusArray = Array.isArray(filter.status)
      ? filter.status
      : [filter.status];
    const filteredStatuses = statusArray.filter((s) => s && s !== 'all');

    if (filteredStatuses.length === 1) {
      // Single status - use $eq
      const strapiStatus =
        filteredStatuses[0] === 'pending'
          ? 'planned'
          : filteredStatuses[0] === 'cancelled'
            ? 'canceled'
            : filteredStatuses[0];
      params.set('filters[orderStatus][$eq]', strapiStatus);
    } else if (filteredStatuses.length > 1) {
      // Multiple statuses - use $in
      filteredStatuses.forEach((status, index) => {
        const strapiStatus =
          status === 'pending'
            ? 'planned'
            : status === 'cancelled'
              ? 'canceled'
              : status;
        params.set(`filters[orderStatus][$in][${index}]`, strapiStatus);
      });
    }
    // If filteredStatuses is empty, don't add any filter (return all)
  }

  // Customer/Tenant filter (server-side)
  if (filter?.customerId) {
    if (isNumericId(filter.customerId)) {
      params.set('filters[tenant][id][$eq]', filter.customerId);
    } else {
      params.set('filters[tenant][documentId][$eq]', filter.customerId);
    }
  }

  // Search filter (server-side)
  // Supports order number + SAP sales order number + related sales order number.
  if (filter?.search) {
    const searchTerm = filter.search.trim();
    if (searchTerm) {
      const terms = searchTerm
        .split(/[,\s]+/)
        .map((t) => t.trim())
        .filter(Boolean);

      if (terms.length > 1) {
        // Multiple terms => exact matching using $in across supported fields.
        terms.forEach((term, i) => {
          params.set(`filters[$or][0][orderNumber][$in][${i}]`, term);
          params.set(`filters[$or][1][sapSalesOrderNumber][$in][${i}]`, term);
          params.set(`filters[$or][2][sales_order][salesOrderNumber][$in][${i}]`, term);
        });
      } else {
        // Single term => partial matching using $containsi across supported fields.
        const term = terms[0];
        params.set('filters[$or][0][orderNumber][$containsi]', term);
        params.set('filters[$or][1][sapSalesOrderNumber][$containsi]', term);
        params.set('filters[$or][2][sales_order][salesOrderNumber][$containsi]', term);
      }
    }
  }

  // Vehicle license plate filter (server-side)
  if (filter?.vehicleLicensePlate) {
    const vehicleLicensePlate = filter.vehicleLicensePlate.trim();
    if (vehicleLicensePlate) {
      params.set(
        'filters[shipment][resource][powerUnit][licensePlate][$containsi]',
        vehicleLicensePlate,
      );
    }
  }

  // Product filter (server-side)
  if (filter?.products && filter.products.length > 0) {
    applyProductsFilter(params, filter.products);
  }

  // Delivery address filter (server-side)
  if (filter?.deliveryAddresses && filter.deliveryAddresses.length > 0) {
    applyDeliveryAddressesFilter(params, filter.deliveryAddresses);
  }

  // Date filters (server-side) — order date maps to createdAt
  if (filter?.dateFrom) {
    params.set('filters[createdAt][$gte]', `${filter.dateFrom}T00:00:00.000Z`);
  }
  if (filter?.dateTo) {
    params.set('filters[createdAt][$lte]', `${filter.dateTo}T23:59:59.999Z`);
  }

  // Delivery date filters (server-side)
  // deliveryDate is a datetime field, so we need full ISO timestamps to cover the
  // entire day. Also, when only a start date is provided (single-day selection),
  // default the end to the same day so the full day is included.
  // Explicit $notNull ensures orders with null deliveryDate are excluded.
  if (filter?.deliveryDateFrom || filter?.deliveryDateTo) {
    const effectiveTo = filter?.deliveryDateTo || filter?.deliveryDateFrom;
    params.set('filters[deliveryDate][$notNull]', 'true');
    if (filter?.deliveryDateFrom) {
      params.set('filters[deliveryDate][$gte]', new Date(`${filter.deliveryDateFrom}T00:00:00`).toISOString());
    }
    if (effectiveTo) {
      params.set('filters[deliveryDate][$lte]', new Date(`${effectiveTo}T23:59:59.999`).toISOString());
    }
  }

  return params;
}

function buildStatsCountParams(
  strapiStatus?: 'planned' | 'processing' | 'delivered',
): URLSearchParams {
  const params = new URLSearchParams();
  params.set('pagination[page]', '1');
  params.set('pagination[pageSize]', '1');
  params.set('fields[0]', 'id');

  if (strapiStatus) {
    params.set('filters[orderStatus][$eq]', strapiStatus);
  }

  return params;
}

async function fetchOrdersCountByStatus(
  http: ReturnType<typeof getHttpClient>,
  strapiStatus?: 'planned' | 'processing' | 'delivered',
): Promise<number> {
  const params = buildStatsCountParams(strapiStatus);
  const response = await http.get<unknown>(`${ORDER_ENDPOINTS.LIST}?${params}`);
  const responseData = response.data as Record<string, unknown> | undefined;
  const meta = responseData?.meta as Record<string, unknown> | undefined;
  const pagination = meta?.pagination as Record<string, unknown> | undefined;
  const total = pagination?.total;

  return typeof total === 'number' && Number.isFinite(total) ? total : 0;
}

async function applySalesOrderFilter(
  params: URLSearchParams,
  salesOrderId: string,
): Promise<void> {
  const trimmed = salesOrderId.trim();
  if (!trimmed) return;

  if (isNumericId(trimmed)) {
    params.set('filters[sales_order][id][$eq]', trimmed);
    return;
  }

  const numericId = await resolveSalesOrderNumericId(trimmed);
  if (numericId) {
    params.set('filters[sales_order][id][$eq]', String(numericId));
    return;
  }

  // Fallback: attempt to filter by documentId directly (may not work for all Strapi relation filters,
  // but it's better than silently returning unrelated orders).
  params.set('filters[sales_order][documentId][$eq]', trimmed);
}

// ============================================
// ID Resolution
// ============================================

async function resolveSalesOrderNumericId(
  salesOrderDocumentId: string,
): Promise<number | null> {
  const http = getHttpClient();

  try {
    const response = await http.get<unknown>(
      ORDER_ENDPOINTS.SALES_ORDER_DETAIL(salesOrderDocumentId),
    );
    const entity = normalizeEntity(response.data);
    const data = (entity as Record<string, unknown>)?.data;
    const inner = normalizeEntity(data);

    const idValue = (inner as Record<string, unknown>)?.id;
    if (typeof idValue === 'number' && Number.isFinite(idValue)) return idValue;
    if (typeof idValue === 'string' && isNumericId(idValue))
      return Number.parseInt(idValue, 10);
  } catch {
    // Fallback to query filter
  }

  try {
    const params = new URLSearchParams();
    params.set('filters[documentId][$eq]', salesOrderDocumentId);
    params.set('pagination[pageSize]', '1');

    const response = await http.get<unknown>(
      `${ORDER_ENDPOINTS.SALES_ORDER_LIST}?${params}`,
    );
    const responseData = (response.data as Record<string, unknown>)?.data;
    const raw = Array.isArray(responseData) ? responseData : [];
    const entity = normalizeEntity(raw[0]);

    const idValue = (entity as Record<string, unknown>)?.id;
    if (typeof idValue === 'number' && Number.isFinite(idValue)) return idValue;
    if (typeof idValue === 'string' && isNumericId(idValue))
      return Number.parseInt(idValue, 10);
    return null;
  } catch {
    return null;
  }
}

async function resolveNumericOrderId(orderId: string): Promise<string> {
  const trimmed =
    typeof orderId === 'string' ? orderId.trim() : String(orderId ?? '').trim();
  if (!trimmed) {
    throw new Error('Order id is required');
  }

  if (isNumericId(trimmed)) {
    return trimmed;
  }

  const http = getHttpClient();
  const params = new URLSearchParams();
  params.set('filters[documentId][$eq]', trimmed);
  params.set('fields[0]', 'id');
  params.set('pagination[page]', '1');
  params.set('pagination[pageSize]', '1');

  const response = await http.get<unknown>(`${ORDER_ENDPOINTS.LIST}?${params}`);
  const responseData = response.data as Record<string, unknown> | undefined;
  const raw = Array.isArray(responseData?.data)
    ? (responseData?.data as unknown[])[0]
    : null;
  const entity = normalizeEntity<{ id?: number }>(raw);
  const resolvedId = entity?.id;

  if (resolvedId == null) {
    throw new Error('Order not found');
  }

  return String(resolvedId);
}

// ============================================
// PDF Helpers
// ============================================

const PDF_MIME_TYPE = 'application/pdf';

function ensurePdfExtension(filename: string): string {
  const trimmed = filename.trim();
  if (!trimmed) return 'order.pdf';
  return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`;
}

function sanitizeFilename(filename: string): string {
  return filename
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractFilenameFromContentDisposition(
  contentDisposition?: string,
): string | null {
  if (!contentDisposition) return null;
  const filenameMatch = contentDisposition.match(
    /filename[^;=\n]*=\s*(?:"([^"]*)"|([^;\n]*))/i,
  );
  if (!filenameMatch) return null;
  const raw = filenameMatch[1] || filenameMatch[2];
  if (!raw) return null;
  return raw.replace(/^["']|["']$/g, '').trim();
}

function resolvePdfFilename(
  orderId: string,
  orderNumber: string | undefined,
  headers: Record<string, string> | undefined,
): string {
  if (orderNumber && orderNumber.trim()) {
    return ensurePdfExtension(sanitizeFilename(orderNumber));
  }

  const headerValue =
    headers?.['content-disposition'] || headers?.['Content-Disposition'];
  const headerFilename = extractFilenameFromContentDisposition(headerValue);
  if (headerFilename) {
    return ensurePdfExtension(sanitizeFilename(headerFilename));
  }

  return ensurePdfExtension(sanitizeFilename(orderId));
}

function stripBase64Prefix(data: string): string {
  const prefixIndex = data.indexOf(',');
  if (data.startsWith('data:') && prefixIndex !== -1) {
    return data.slice(prefixIndex + 1);
  }
  return data;
}

function decodeBase64ToBytes(data: string): Uint8Array {
  const normalized = stripBase64Prefix(data).replace(/\s/g, '');
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function createPdfBlob(data: unknown): Blob {
  if (data instanceof Blob) return data;
  if (data instanceof ArrayBuffer)
    return new Blob([data], { type: PDF_MIME_TYPE });
  if (ArrayBuffer.isView(data))
    return new Blob([data.buffer], { type: PDF_MIME_TYPE });
  if (typeof data === 'string') {
    try {
      return new Blob([decodeBase64ToBytes(data)], { type: PDF_MIME_TYPE });
    } catch {
      return new Blob([data], { type: PDF_MIME_TYPE });
    }
  }
  return new Blob([JSON.stringify(data ?? '')], { type: PDF_MIME_TYPE });
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(downloadUrl);
  document.body.removeChild(link);
}

function triggerBlobPrint(blob: Blob): void {
  const url = window.URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.cssText =
    'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;';
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 60000);
    }
  };
}

async function fetchOrderPdfPayload(
  orderId: string,
  orderNumber?: string,
): Promise<{ blob: Blob; filename: string }> {
  const http = getHttpClient();
  const resolvedOrderId = await resolveNumericOrderId(orderId);
  const url = `/api/fulfillment/order/${resolvedOrderId}/pdf`;

  console.log(
    '[ordersApi.fetchOrderPdfPayload] Downloading PDF for order:',
    orderId,
    'resolvedId:',
    resolvedOrderId,
  );

  const response = await http.get(url, {
    responseType: 'blob',
  });

  const headers = (response.headers ?? {}) as Record<string, string>;
  const filename = resolvePdfFilename(orderId, orderNumber, headers);
  const blob = createPdfBlob(response.data);

  return { blob, filename };
}

async function fetchOrderDeliveryNotePayload(
  orderId: string,
  orderNumber?: string,
  style: 'color' | 'bw' = 'color',
): Promise<{ blob: Blob; filename: string }> {
  const http = getHttpClient();
  const resolvedOrderId = await resolveNumericOrderId(orderId);
  const url = `/api/fulfillment/order/${resolvedOrderId}/delivery-note?style=${style}`;

  console.log(
    '[ordersApi.fetchOrderDeliveryNotePayload] Downloading delivery note for order:',
    orderId,
    'resolvedId:',
    resolvedOrderId,
    'style:',
    style,
  );

  const response = await http.get(url, {
    responseType: 'blob',
  });

  const headers = (response.headers ?? {}) as Record<string, string>;
  const filename = resolvePdfFilename(orderId, orderNumber, headers);
  const blob = createPdfBlob(response.data);

  return { blob, filename };
}

async function fetchOrderPdfBWPayload(
  orderId: string,
  orderNumber?: string,
): Promise<{ blob: Blob; filename: string }> {
  const http = getHttpClient();
  const resolvedOrderId = await resolveNumericOrderId(orderId);
  const url = `/api/fulfillment/order/${resolvedOrderId}/pdf-bw`;

  console.log(
    '[ordersApi.fetchOrderPdfBWPayload] Downloading B&W PDF for order:',
    orderId,
    'resolvedId:',
    resolvedOrderId,
  );

  const response = await http.get(url, {
    responseType: 'blob',
  });

  const headers = (response.headers ?? {}) as Record<string, string>;
  const filename = resolvePdfFilename(orderId, orderNumber, headers);
  const blob = createPdfBlob(response.data);

  return { blob, filename };
}

async function fetchMultipleOrdersPdfPayload(
  orderIds: string[],
): Promise<{ blob: Blob; filename: string }> {
  if (!orderIds || orderIds.length === 0) {
    throw new Error('At least one order ID is required');
  }

  const http = getHttpClient();

  const resolvedIds = await Promise.all(
    orderIds.map(async (orderId) => {
      try {
        return await resolveNumericOrderId(orderId);
      } catch {
        console.warn(
          `[ordersApi.fetchMultipleOrdersPdfPayload] Could not resolve order ID: ${orderId}`,
        );
        return orderId;
      }
    }),
  );

  console.log(
    '[ordersApi.fetchMultipleOrdersPdfPayload] Downloading PDF for orders:',
    resolvedIds,
  );

  const response = await http.post(
    '/api/fulfillment/orders/pdf',
    {
      orderIds: resolvedIds,
    },
    {
      responseType: 'blob',
    },
  );

  const headers = (response.headers ?? {}) as Record<string, string>;
  const contentDisposition =
    headers['content-disposition'] || headers['Content-Disposition'];
  const headerFilename =
    extractFilenameFromContentDisposition(contentDisposition);
  const fallbackFilename = `Orders-${new Date().toISOString().slice(0, 10)}.pdf`;
  const filename = ensurePdfExtension(
    sanitizeFilename(headerFilename || fallbackFilename),
  );
  const blob = createPdfBlob(response.data);

  return { blob, filename };
}

// ============================================
// API Client
// ============================================

export const ordersApi = {
  /**
   * Get paginated orders with pagination metadata
   * All filtering and pagination is server-side
   */
  async getOrdersPaginated(filter?: OrdersFilter): Promise<PaginatedOrders> {
    const http = getHttpClient();
    const params = buildListParams(filter);

    if (filter?.salesOrderId) {
      await applySalesOrderFilter(params, filter.salesOrderId);
    }

    const response = await http.get<unknown>(
      `${ORDER_ENDPOINTS.LIST}?${params}`,
    );
    const responseData = response.data as Record<string, unknown>;
    const raw = Array.isArray(responseData?.data)
      ? (responseData.data as unknown[])
      : [];

    const orders = transformOrdersResponseToDomain(raw, filter?.salesOrderId);
    const shouldSortClientSide = filter?.sortBy
      ? CLIENT_SORTABLE_FIELDS.has(filter.sortBy)
      : false;
    const sortedOrders = shouldSortClientSide
      ? sortOrdersClientSide(
          orders,
          filter?.sortBy as string,
          (filter?.sortOrder ?? 'desc') as 'asc' | 'desc',
        )
      : orders;

    const meta = responseData?.meta as Record<string, unknown> | undefined;
    const paginationData = meta?.pagination as
      | Record<string, unknown>
      | undefined;

    const pagination: PaginationMeta = {
      page: (paginationData?.page as number) ?? filter?.page ?? 1,
      pageSize:
        (paginationData?.pageSize as number) ??
        filter?.pageSize ??
        DEFAULT_PAGE_SIZE,
      pageCount: (paginationData?.pageCount as number) ?? 1,
      total: (paginationData?.total as number) ?? sortedOrders.length,
    };

    // Extract inline stats from meta (computed by Strapi using same filters minus status)
    const statsData = meta?.stats as Record<string, unknown> | undefined;
    const stats = statsData
      ? {
          pending: typeof statsData.pending === 'number' ? statsData.pending : 0,
          processing: typeof statsData.processing === 'number' ? statsData.processing : 0,
          delivered: typeof statsData.delivered === 'number' ? statsData.delivered : 0,
          total: typeof statsData.total === 'number' ? statsData.total : 0,
        }
      : undefined;

    return { data: sortedOrders, pagination, stats };
  },

  /**
   * Get orders (backwards compatible - returns array only)
   */
  async getOrders(filter?: OrdersFilter): Promise<Order[]> {
    const result = await ordersApi.getOrdersPaginated(filter);
    return result.data;
  },

  async getOrder(id: string): Promise<Order | null> {
    const http = getHttpClient();
    const params = buildPopulateParams();

    // Try by documentId or numeric id
    if (isNumericId(id)) {
      params.set('filters[id][$eq]', id);
    } else {
      params.set('filters[documentId][$eq]', id);
    }

    try {
      const response = await http.get<unknown>(
        `${ORDER_ENDPOINTS.LIST}?${params}`,
      );
      const raw = Array.isArray(
        (response.data as Record<string, unknown>)?.data,
      )
        ? ((response.data as Record<string, unknown>).data as unknown[])
        : [];

      if (raw.length === 0 && !isNumericId(id)) {
        // Fallback: try by numeric id
        const fallbackParams = buildPopulateParams();
        fallbackParams.set('filters[id][$eq]', id);

        const fallbackResponse = await http.get<unknown>(
          `${ORDER_ENDPOINTS.LIST}?${fallbackParams}`,
        );
        const fallbackRaw = Array.isArray(
          (fallbackResponse.data as Record<string, unknown>)?.data,
        )
          ? ((fallbackResponse.data as Record<string, unknown>)
              .data as unknown[])
          : [];

        if (fallbackRaw.length > 0) {
          return transformOrderResponseToDomain(fallbackRaw[0]);
        }
      }

      if (raw.length > 0) {
        return transformOrderResponseToDomain(raw[0]);
      }

      return null;
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get order statistics
   * Single request to /api/orders/stats — collapses 4 previous count requests into 1.
   */
  async getOrderStats(): Promise<OrderStats> {
    const http = getHttpClient();
    const response = await http.get<{ data: { pending: number; processing: number; delivered: number; total: number } }>(
      ORDER_ENDPOINTS.STATS,
    );
    const stats = (response.data as Record<string, unknown>)?.data as Record<string, unknown> | undefined;
    return validateOrderStats({
      pending: typeof stats?.pending === 'number' ? stats.pending : 0,
      processing: typeof stats?.processing === 'number' ? stats.processing : 0,
      delivered: typeof stats?.delivered === 'number' ? stats.delivered : 0,
      total: typeof stats?.total === 'number' ? stats.total : 0,
    });
  },

  /**
   * Create new order
   */
  async createOrder(data: CreateOrderRequest): Promise<Order> {
    const http = getHttpClient();

    const orderData: Record<string, unknown> = {
      quantity: data.items[0]?.quantity.value || 0,
      deliveryDate: data.deliveryDate,
      orderStatus: 'planned',
      pricingSnapshot: {
        orderNumber: `ORD-${Date.now()}`,
        productName: data.items[0]?.product || { en: 'Product', ar: 'منتج' },
        unitPrice: {
          amount: data.items[0]?.unitPrice.amount || 0,
          currency: data.items[0]?.unitPrice.currency || 'SAR',
        },
        packaging: data.items[0]?.quantity.unit === 'tons' ? 'bulk' : 'bags',
      },
      metadata: {
        notes: data.notes,
        deliveryAddress: data.deliveryAddress,
      },
    };

    // Link to sales order if contractId provided
    if (data.contractId) {
      const salesOrderId = await resolveSalesOrderNumericId(data.contractId);
      if (salesOrderId) {
        orderData.sales_order = salesOrderId;
      }
    }

    // Set tenant from customerId
    if (data.customerId) {
      orderData.tenant = coerceToNumberId(data.customerId) ?? data.customerId;
    }

    const response = await http.post<unknown>(ORDER_ENDPOINTS.CREATE, {
      data: orderData,
    });
    const createdEntity = normalizeEntity(
      (response.data as Record<string, unknown>)?.data,
    );

    // Fetch the full order with populated relations
    const createdId = String(
      (createdEntity as Record<string, unknown>)?.documentId ??
        (createdEntity as Record<string, unknown>)?.id ??
        '',
    );
    const fullOrder = await ordersApi.getOrder(createdId);

    return fullOrder || transformOrderResponseToDomain(createdEntity);
  },

  async updateOrder(id: string, data: UpdateOrderRequest): Promise<Order> {
    const http = getHttpClient();

    // Fetch existing order to preserve metadata
    const currentOrder = await ordersApi.getOrder(id);
    if (!currentOrder) {
      throw new Error('Order not found');
    }

    const updateData: Record<string, unknown> = {};

    if (data.deliveryDate) {
      updateData.deliveryDate = data.deliveryDate;
    }

    if (data.deliveryAddress || data.notes !== undefined) {
      updateData.metadata = {
        deliveryAddress: data.deliveryAddress,
        notes: data.notes,
      };
    }

    // Sub-customer relation: pass documentId or null to connect/disconnect
    if ('subCustomer' in data) {
      updateData.subCustomer = data.subCustomer ?? null;
    }

    const response = await http.put<unknown>(ORDER_ENDPOINTS.UPDATE(id), {
      data: updateData,
    });
    const updatedEntity = normalizeEntity(
      (response.data as Record<string, unknown>)?.data,
    );

    // Fetch the full order with populated relations
    const updatedId = String(
      (updatedEntity as Record<string, unknown>)?.documentId ??
        (updatedEntity as Record<string, unknown>)?.id ??
        id,
    );
    const fullOrder = await ordersApi.getOrder(updatedId);

    return fullOrder || transformOrderResponseToDomain(updatedEntity);
  },

  async cancelOrder(id: string, reason: string): Promise<Order> {
    const http = getHttpClient();

    const currentOrder = await ordersApi.getOrder(id);
    if (!currentOrder) {
      throw new Error('Order not found');
    }

    if (currentOrder.status !== 'pending') {
      throw new Error('Cannot cancel order in current status');
    }

    const response = await http.put<unknown>(ORDER_ENDPOINTS.UPDATE(id), {
      data: {
        orderStatus: 'canceled',
        cancellationReason: reason,
      },
    });
    const updatedEntity = normalizeEntity(
      (response.data as Record<string, unknown>)?.data,
    );

    // Fetch the full order with populated relations
    const updatedId = String(
      (updatedEntity as Record<string, unknown>)?.documentId ??
        (updatedEntity as Record<string, unknown>)?.id ??
        id,
    );
    const fullOrder = await ordersApi.getOrder(updatedId);

    return fullOrder || transformOrderResponseToDomain(updatedEntity);
  },

  async bulkCancelOrders(
    orderIds: string[],
    reason: string,
  ): Promise<BulkCancelOrdersResult> {
    const http = getHttpClient();
    const response = await http.post<unknown>(ORDER_ENDPOINTS.BULK_CANCEL, {
      orderIds,
      reason,
    });

    const data = (response.data as Record<string, unknown> | undefined)
      ?.data as Record<string, unknown> | undefined;
    const succeededOrderIds = Array.isArray(data?.succeededOrderIds)
      ? data?.succeededOrderIds.map((item) => String(item))
      : [];
    const failed = Array.isArray(data?.failed)
      ? data?.failed.map((item) => {
          const failure = item as Record<string, unknown>;
          return {
            orderId: String(failure?.orderId ?? ''),
            message: String(failure?.message ?? 'Failed'),
          };
        })
      : [];

    return {
      successCount:
        typeof data?.successCount === 'number'
          ? data.successCount
          : succeededOrderIds.length,
      failureCount:
        typeof data?.failureCount === 'number'
          ? data.failureCount
          : failed.length,
      succeededOrderIds,
      failed,
    };
  },

  async bulkForceUpdateStatus(
    orderIds: string[],
    newStatus: string,
    reason: string,
    incidentRef: string | undefined,
    affectedOpsNote: string | undefined,
    forceJobId: string,
  ): Promise<BulkForceUpdateStatusResult> {
    const http = getHttpClient();
    const response = await http.post<unknown>(ORDER_ENDPOINTS.BULK_FORCE_STATUS, {
      orderIds,
      newStatus,
      reason,
      incidentRef: incidentRef || undefined,
      affectedOpsNote: affectedOpsNote || undefined,
      forceJobId,
    });

    const data = (response.data as Record<string, unknown> | undefined)
      ?.data as Record<string, unknown> | undefined;
    const succeededOrderIds = Array.isArray(data?.succeededOrderIds)
      ? data?.succeededOrderIds.map((item) => String(item))
      : [];
    const failed = Array.isArray(data?.failed)
      ? data?.failed.map((item) => {
          const failure = item as Record<string, unknown>;
          return {
            orderId: String(failure?.orderId ?? ''),
            message: String(failure?.message ?? 'Failed'),
          };
        })
      : [];

    return {
      successCount:
        typeof data?.successCount === 'number'
          ? data.successCount
          : succeededOrderIds.length,
      failureCount:
        typeof data?.failureCount === 'number'
          ? data.failureCount
          : failed.length,
      succeededOrderIds,
      failed,
    };
  },

  /**
   * Get orders by sales order ID (server-side filtering)
   * Returns all orders for a given sales order with server-side pagination
   */
  async getOrdersBySalesOrderId(
    salesOrderId: string,
    page?: number,
    pageSize?: number,
  ): Promise<Order[]> {
    const result = await ordersApi.getOrdersPaginated({
      salesOrderId,
      page: page ?? 1,
      pageSize: pageSize ?? MAX_PAGE_SIZE,
    });
    return result.data;
  },

  /**
   * Get paginated orders by sales order ID (server-side)
   */
  async getOrdersBySalesOrderIdPaginated(
    salesOrderId: string,
    page?: number,
    pageSize?: number,
  ): Promise<PaginatedOrders> {
    return ordersApi.getOrdersPaginated({
      salesOrderId,
      page: page ?? 1,
      pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
    });
  },

  /**
   * Download order details PDF
   * @param orderId - Order ID or documentId
   * @param orderNumber - Optional order number for filename (if not provided, will try to extract from header)
   * @returns Promise that triggers browser download
   */
  async downloadOrderPDF(orderId: string, orderNumber?: string): Promise<void> {
    try {
      const { blob, filename } = await fetchOrderPdfPayload(
        orderId,
        orderNumber,
      );

      console.log('[ordersApi.downloadOrderPDF] Final filename:', filename);

      triggerBlobDownload(blob, filename);

      console.log(
        '[ordersApi.downloadOrderPDF] PDF downloaded successfully:',
        filename,
      );
    } catch (error) {
      console.error(
        '[ordersApi.downloadOrderPDF] Error downloading PDF:',
        error,
      );
      throw new Error('Failed to download order PDF');
    }
  },

  async downloadOrderDeliveryNote(
    orderId: string,
    orderNumber?: string,
  ): Promise<void> {
    try {
      const { blob, filename } = await fetchOrderDeliveryNotePayload(
        orderId,
        orderNumber,
        'color',
      );

      console.log(
        '[ordersApi.downloadOrderDeliveryNote] Final filename:',
        filename,
      );

      triggerBlobDownload(blob, filename);
    } catch (error) {
      console.error(
        '[ordersApi.downloadOrderDeliveryNote] Error downloading delivery note:',
        error,
      );
      throw new Error('Failed to download order delivery note');
    }
  },

  /**
   * Print order details PDF via system printer dialog
   * @param orderId - Order ID or documentId
   * @param orderNumber - Optional order number for logging
   * @returns Promise that triggers browser print dialog
   */
  async printOrderPDF(orderId: string, orderNumber?: string): Promise<void> {
    try {
      const { blob } = await fetchOrderPdfPayload(orderId, orderNumber);

      console.log(
        '[ordersApi.printOrderPDF] Triggering print for order:',
        orderNumber ?? orderId,
      );

      triggerBlobPrint(blob);

      console.log(
        '[ordersApi.printOrderPDF] Print dialog triggered successfully',
      );
    } catch (error) {
      console.error('[ordersApi.printOrderPDF] Error printing PDF:', error);
      throw new Error('Failed to print order PDF');
    }
  },

  /**
   * Print B&W (printer-friendly) order PDF via system printer dialog
   * Used by the kiosk portal for thermal printers.
   */
  async printOrderPDFBW(orderId: string, orderNumber?: string): Promise<void> {
    try {
      const { blob } = await fetchOrderPdfBWPayload(orderId, orderNumber);

      console.log(
        '[ordersApi.printOrderPDFBW] Triggering B&W print for order:',
        orderNumber ?? orderId,
      );

      triggerBlobPrint(blob);

      console.log(
        '[ordersApi.printOrderPDFBW] B&W print dialog triggered successfully',
      );
    } catch (error) {
      console.error('[ordersApi.printOrderPDFBW] Error printing B&W PDF:', error);
      throw new Error('Failed to print B&W order PDF');
    }
  },

  /**
   * Print order delivery note via an 80mm thermal printer (B&W, portrait).
   * The delivery note is generated as portrait 80mm — no rotation needed.
   */
  async printOrderDeliveryNote80mm(orderId: string, orderNumber?: string): Promise<void> {
    try {
      const { blob } = await fetchOrderDeliveryNotePayload(orderId, orderNumber, 'bw');

      console.log(
        '[ordersApi.printOrderDeliveryNote80mm] Printing B&W delivery note for order:',
        orderNumber ?? orderId,
      );

      triggerBlobPrint(blob);

      console.log(
        '[ordersApi.printOrderDeliveryNote80mm] Print dialog triggered successfully',
      );
    } catch (error) {
      console.error('[ordersApi.printOrderDeliveryNote80mm] Error printing delivery note:', error);
      throw new Error('Failed to print delivery note (80mm)');
    }
  },

  /**
   * Fetch order PDF payload for in-app preview or sharing
   * @param orderId - Order ID or documentId
   * @param orderNumber - Optional order number for filename
   */
  async getOrderPDFPayload(
    orderId: string,
    orderNumber?: string,
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      return await fetchOrderPdfPayload(orderId, orderNumber);
    } catch (error) {
      console.error(
        '[ordersApi.getOrderPDFPayload] Error fetching PDF:',
        error,
      );
      throw new Error('Failed to fetch order PDF');
    }
  },

  /**
   * Fetch delivery note PDF for in-app preview or sharing (native / Capacitor)
   */
  async getOrderDeliveryNotePayload(
    orderId: string,
    orderNumber?: string,
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      return await fetchOrderDeliveryNotePayload(orderId, orderNumber, 'color');
    } catch (error) {
      console.error(
        '[ordersApi.getOrderDeliveryNotePayload] Error fetching delivery note:',
        error,
      );
      throw new Error('Failed to fetch delivery note PDF');
    }
  },

  /**
   * Fetch multiple orders PDF payload for sharing
   * @param orderIds - Array of order IDs or documentIds
   */
  async getMultipleOrdersPDFPayload(
    orderIds: string[],
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      return await fetchMultipleOrdersPdfPayload(orderIds);
    } catch (error) {
      console.error(
        '[ordersApi.getMultipleOrdersPDFPayload] Error fetching PDF:',
        error,
      );
      throw new Error('Failed to fetch orders PDF');
    }
  },

  /**
   * Download multiple orders as a single merged PDF
   * @param orderIds - Array of order IDs or documentIds
   * @returns Promise that triggers browser download
   */
  async downloadMultipleOrdersPDF(orderIds: string[]): Promise<void> {
    try {
      const payload = await fetchMultipleOrdersPdfPayload(orderIds);
      triggerBlobDownload(payload.blob, payload.filename);
      console.log(
        '[ordersApi.downloadMultipleOrdersPDF] PDF downloaded successfully:',
        payload.filename,
      );
    } catch (error) {
      console.error(
        '[ordersApi.downloadMultipleOrdersPDF] Error downloading PDF:',
        error,
      );
      throw new Error('Failed to download orders PDF');
    }
  },

  /**
   * Export all orders matching current filters (no pagination limit)
   */
  async exportAll(filter?: OrdersFilter): Promise<Order[]> {
    const http = getHttpClient();
    const params = new URLSearchParams();

    if (filter?.status) {
      const statusArray = Array.isArray(filter.status)
        ? filter.status
        : [filter.status];
      const filtered = statusArray.filter((s) => s && s !== 'all');
      if (filtered.length === 1) {
        const mapped =
          filtered[0] === 'pending'
            ? 'planned'
            : filtered[0] === 'cancelled'
              ? 'canceled'
              : filtered[0];
        params.set('status', mapped);
      }
    }
    if (filter?.search) params.set('search', filter.search);
    if (filter?.dateFrom) params.set('dateFrom', filter.dateFrom);
    if (filter?.dateTo) params.set('dateTo', filter.dateTo);
    if (filter?.deliveryDateFrom) params.set('deliveryDateFrom', filter.deliveryDateFrom);
    if (filter?.deliveryDateTo) params.set('deliveryDateTo', filter.deliveryDateTo);
    if (filter?.products && filter.products.length > 0) {
      params.set('products', filter.products.join(','));
    }
    if (filter?.deliveryAddresses && filter.deliveryAddresses.length > 0) {
      params.set('deliveryAddresses', filter.deliveryAddresses.join(','));
    }
    if (filter?.vehicleLicensePlate) {
      params.set('vehicleLicensePlate', filter.vehicleLicensePlate);
    }

    const response = await http.get<unknown>(
      `${ORDER_ENDPOINTS.LIST}/export?${params}`,
    );
    const responseData = response.data as Record<string, unknown>;
    const raw = Array.isArray(responseData?.data)
      ? (responseData.data as unknown[])
      : [];
    return transformOrdersResponseToDomain(raw);
  },

  /**
   * Fetch the full audit timeline for an order.
   * Returns all events linked to the order, sorted newest first.
   */
  async getOrderTimeline(orderId: string): Promise<OrderEvent[]> {
    const http = getHttpClient();
    try {
      const response = await http.get<unknown>(
        `/api/orders/${orderId}/timeline`,
      );
      const data = response.data as Record<string, unknown>;
      const items = Array.isArray(data?.data)
        ? (data.data as Record<string, unknown>[])
        : [];
      return items.map(transformEventDto);
    } catch {
      return [];
    }
  },

  /**
   * POST /orders/:id/send-sms
   * Manually send an SMS with the order PDF link to the assigned driver.
   * Issues a fresh token each call so the link is always valid.
   */
  async sendDriverSms(orderId: string | number): Promise<void> {
    const http = getHttpClient();
    const id = String(orderId);
    await http.post<unknown>(ORDER_ENDPOINTS.SEND_SMS(id));
  },

  /**
   * Fetch all variant options (id + name in EN & AR) for filter dropdowns.
   * Single locale=en request — AR names come from the localizations relation.
   */
  async getVariantOptions(): Promise<Array<{ value: string; en: string; ar?: string }>> {
    const http = getHttpClient();
    const params = new URLSearchParams({
      locale: 'en',
      'fields[0]': 'documentId',
      'fields[1]': 'name',
      'fields[2]': 'variantCode',
      'filters[deletedAt][$null]': 'true',
      'pagination[pageSize]': '500',
      sort: 'name:asc',
      'populate[localizations][fields][0]': 'name',
      'populate[localizations][fields][1]': 'locale',
    });

    try {
      const res = await http.get<{
        data: Array<{
          documentId: string;
          name?: string;
          variantCode?: string;
          localizations?: Array<{ name?: string; locale?: string }>;
        }>;
      }>(`/api/variants?${params}`);

      return res.data.data
        .filter((item) => item.documentId && (item.name || item.variantCode))
        .map((item) => {
          const arLocalization = item.localizations?.find((l) => l.locale === 'ar');
          return {
            value: item.documentId,
            en: item.name || item.variantCode || item.documentId,
            ar: arLocalization?.name,
          };
        });
    } catch {
      return [];
    }
  },
};

function transformEventDto(raw: Record<string, unknown>): OrderEvent {
  // Strapi v5 returns flat objects; v4 wraps in attributes — handle both
  const attrs = (raw.attributes as Record<string, unknown>) ?? raw;
  return {
    id: raw.id as number,
    entityType: (attrs.entityType ??
      raw.entityType) as OrderEvent['entityType'],
    entityId: String(attrs.entityId ?? raw.entityId ?? ''),
    action: (attrs.action ?? raw.action) as OrderEvent['action'],
    description: (attrs.description ?? raw.description ?? null) as
      | string
      | null,
    metadata: (attrs.metadata ?? raw.metadata ?? null) as Record<
      string,
      unknown
    > | null,
    createdAt: String(attrs.createdAt ?? raw.createdAt ?? ''),
  };
}
